/**
 * PlaybookBook3D — the blue-leather Playbook book in the hero's Playbook
 * button. At rest it's a closed, ornate leather book with "Playbook" stamped
 * in gold; on hover the front cover unfolds — slowly, with an ease-in-out —
 * into a two-page spread. Move away and it closes the same way. Clicks bubble
 * up to the button (which navigates to /playbook).
 *
 * The GLB carries the whole animation (cover fold + a small centering slide).
 * We don't play it as a loop — we SCRUB it with a 0..1 hover progress so the
 * open/close feel is fully under our control. Follows the <Crown3D /> pattern:
 * transparent canvas, auto-fit + center via a wrapper group.
 */
/* eslint-disable react/no-unknown-property -- r3f uses lowercase JSX props */
import React, { Suspense, useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { Canvas, useFrame } from "@react-three/fiber";
import { useGLTF } from "@react-three/drei";
import bookGlb from "../../assets/models/playbook_book.glb";

const OPEN_DUR = 1.15; // seconds to fully open — slow & graceful

function BookMesh({ hoveredRef }) {
  const { scene, animations } = useGLTF(bookGlb);

  // Our own mixer, one action per clip (the GLB has a couple of same-named
  // clips, so we build actions from the array rather than a name-keyed dict).
  const mixer = useMemo(() => new THREE.AnimationMixer(scene), [scene]);
  const actions = useMemo(
    () => (animations || []).map((clip) => mixer.clipAction(clip)),
    [animations, mixer],
  );
  const openT = useMemo(
    () => Math.max(0.001, ...(animations || []).map((c) => c.duration || 0)),
    [animations],
  );

  useEffect(() => {
    actions.forEach((a) => {
      a.reset();
      a.play();
      a.paused = true;
      a.clampWhenFinished = true;
      a.setLoop(THREE.LoopOnce, 1);
      a.time = 0;
    });
    mixer.update(0);
    return () => actions.forEach((a) => a.stop());
  }, [actions, mixer]);

  // Auto-fit AND center on the CLOSED pose (same stability trick as Crown3D:
  // reset the cached scene to identity, measure, apply fit to a wrapper group).
  const fitScale = useMemo(() => {
    try {
      scene.position.set(0, 0, 0);
      scene.rotation.set(0, 0, 0);
      scene.scale.set(1, 1, 1);
      scene.updateMatrixWorld(true);
      const b = new THREE.Box3().setFromObject(scene);
      const size = b.getSize(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.y, size.z) || 1;
      return 1.7 / maxDim; // centering is done live in useFrame
    } catch {
      return 1;
    }
  }, [scene]);

  // Scrub the animation with a 0..1 hover progress, eased in/out (smoothstep),
  // then live re-center so the book stays dead-center whether closed, opening,
  // or open — no sideways drift, and the open spread lands centered too.
  const progress = useRef(0);
  const centerRef = useRef();
  const box = useMemo(() => new THREE.Box3(), []);
  const cen = useMemo(() => new THREE.Vector3(), []);
  useFrame((state, delta) => {
    const target = hoveredRef.current ? 1 : 0;
    const step = Math.min(delta, 0.05) / OPEN_DUR;
    progress.current = progress.current < target
      ? Math.min(target, progress.current + step)
      : Math.max(target, progress.current - step);
    const p = progress.current;
    const e = p * p * (3 - 2 * p); // smoothstep
    const t = Math.max(0.0001, e * openT);
    for (let i = 0; i < actions.length; i += 1) actions[i].time = t;
    mixer.update(0);
    if (centerRef.current) {
      box.setFromObject(centerRef.current);
      box.getCenter(cen);
      centerRef.current.position.sub(cen); // world center already includes the offset
      // gentle idle: a slow breathing bob + soft sway so it feels alive
      const tt = state.clock.elapsedTime;
      centerRef.current.position.y += Math.sin(tt * 1.15) * 0.045;
      centerRef.current.rotation.y = Math.sin(tt * 0.6) * 0.16;
      centerRef.current.rotation.x = -0.02 + Math.sin(tt * 0.9) * 0.035;
    }
  });

  return (
    <group ref={centerRef}>
      <group scale={fitScale}>
        <primitive object={scene} />
      </group>
    </group>
  );
}

export default function PlaybookBook3D({ size = 64 }) {
  const hoveredRef = useRef(false);
  return (
    <span
      style={{ position: "relative", width: size, height: size, display: "inline-block", flexShrink: 0 }}
      onMouseEnter={() => { hoveredRef.current = true; }}
      onMouseLeave={() => { hoveredRef.current = false; }}
    >
      {/* warm gold halo so the navy book lifts off the blue hero (and echoes
          its own gold trim). Sits behind the transparent canvas. */}
      <span
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: "-16%",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(246,205,120,0.5), rgba(246,205,120,0) 66%)",
          filter: "blur(4px)",
          pointerEvents: "none",
        }}
      />
      <Canvas
        camera={{ position: [0, 0.45, 3.35], fov: 36 }}
        gl={{ alpha: true, antialias: true, preserveDrawingBuffer: false }}
        style={{ background: "transparent" }}
        dpr={[1, 2]}
        onCreated={({ camera }) => camera.lookAt(0, 0, 0)}
      >
        {/* Warm key + cool fill so the blue leather + gold read on the hero. */}
        <ambientLight intensity={0.95} />
        <directionalLight position={[3, 5, 4]} intensity={1.55} color="#FFF2E0" />
        <directionalLight position={[-4, 2, 2]} intensity={0.55} color="#BFD2F5" />
        <hemisphereLight args={["#FFF6E5", "#2A3A55", 0.85]} />

        <Suspense fallback={null}>
          <BookMesh hoveredRef={hoveredRef} />
        </Suspense>
      </Canvas>
    </span>
  );
}

// Pre-cache so the hero doesn't flash an empty button on first paint.
useGLTF.preload(bookGlb);
